import type { ReasoningItem } from "@/domain/entities/vistassistant/AIResponse";
import { cn } from "@/lib/utils";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

interface ReasoningBlockProps {
    items: ReasoningItem[];
    /**
     * Turn still running: shown open, with a spinner. Once the answer arrives, folded, like
     * Claude's « Réflexion »: the steps, then the model's thoughts (a turn without a step still
     * shows its thoughts).
     */
    inProgress?: boolean;
}

// Reasoning of a step-based turn, above the assistant answer: one discreet line that unfolds. A
// step is written by the Api-Ai (what was read, computed, refused); a thought is the model's raw
// text. Display only: the SDK never sends it back nor keeps it.
const ReasoningBlock = ({ items, inProgress = false }: ReasoningBlockProps) => {
    const { t } = useTranslation();
    const listId = useId();
    const [isOpen, setIsOpen] = useState(inProgress);
    // Steps first, then the thoughts.
    const steps = items.filter((item) => item.kind === "step");
    const shown = inProgress
        ? items
        : [...steps, ...items.filter((item) => item.kind !== "step")];

    if (shown.length === 0) return null;

    return (
        <div className="flex w-full flex-col gap-1.5">
            <button
                aria-controls={listId}
                aria-expanded={isOpen}
                className="flex min-h-5 w-fit items-center gap-2 rounded-md text-sm font-semibold text-assistant-muted transition-colors hover:text-primary-darkest"
                onClick={() => setIsOpen((open) => !open)}
                type="button"
            >
                {inProgress ? (
                    <Loader2 aria-hidden className="size-4 animate-spin" />
                ) : (
                    <ChevronRight
                        aria-hidden
                        className={cn(
                            "size-4 transition-transform",
                            isOpen && "rotate-90",
                        )}
                        strokeWidth={1.5}
                    />
                )}
                {inProgress
                    ? t("vistassistant.reasoning.inProgress")
                    : steps.length > 0
                      ? t("vistassistant.reasoning.title", {
                            count: steps.length,
                        })
                      : t("vistassistant.reasoning.thinking")}
            </button>
            {isOpen && (
                <ol
                    className="ml-2 flex flex-col gap-1.5 border-l border-assistant-cardBorder pl-4 text-sm leading-snug text-assistant-muted"
                    id={listId}
                >
                    {shown.map((item, index) =>
                        item.kind === "step" ? (
                            <li
                                // Lines only grow during a turn: the index is stable.
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                className="flex items-start gap-1.5"
                            >
                                <Check
                                    aria-hidden
                                    className="mt-0.5 h-3.5 w-3.5 flex-none text-primary-dark"
                                />
                                <span>{item.text}</span>
                            </li>
                        ) : (
                            <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                className="max-h-48 overflow-y-auto whitespace-pre-wrap italic"
                            >
                                {item.text}
                            </li>
                        ),
                    )}
                </ol>
            )}
        </div>
    );
};

export default ReasoningBlock;
